import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface SchoolUser {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  role: {
    id: string;
    name: string;
    code: string;
  };
}

/**
 * Create School Admin Component
 * Allows SuperAdmin to create admin users for schools
 */
const CreateSchoolAdmin: React.FC<{ schoolId: string; onSuccess: () => void }> = ({
  schoolId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState<SchoolUser[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<SchoolUser | null>(null);
  const [editData, setEditData] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    phone: '',
    isActive: true,
  });
  const [resetMessage, setResetMessage] = useState('');

  const fetchUsers = useCallback(async () => {
    setListError('');
    setListLoading(true);
    try {
      const response = await axios.get(`/system-settings/users?schoolId=${schoolId}`);
      setUsers(response.data || []);
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to load users/admins');
    } finally {
      setListLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    if (schoolId) {
      fetchUsers();
    }
  }, [schoolId, fetchUsers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await axios.post(`/schools/${schoolId}/admin`, formData);
      setSuccess(true);
      setFormData({
        email: '',
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
      });
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 2000);
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user: SchoolUser) => {
    setEditTarget(user);
    setEditData({
      email: user.email,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || '',
      isActive: user.isActive,
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    setError('');

    try {
      await axios.patch(`/schools/${schoolId}/admins/${editTarget.id}`, editData);
      setEditOpen(false);
      setEditTarget(null);
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update protected admin');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetProtectedAdmin = async (user: SchoolUser) => {
    const confirmReset = window.confirm(
      `Reset password for ${user.firstName} ${user.lastName}?`,
    );
    if (!confirmReset) return;

    setError('');
    setResetMessage('');

    try {
      const response = await axios.post(`/schools/${schoolId}/admins/${user.id}/reset-password`);
      setResetMessage(response.data?.message || 'Password reset processed.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset protected admin password');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create School Admin
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Create a protected admin for this school. The entered username becomes this school's login username for all users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Admin created successfully!
        </Alert>
      )}

      {resetMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResetMessage('')}>
          {resetMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Admin'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          School Users & Admins
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          All created users/admins for this school are listed below. Edit and reset actions are available only for Protected Admin.
        </Typography>

        {listError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError('')}>
            {listError}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const isProtectedAdmin = user.role?.code === 'ADMIN';
                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${user.role?.name} (${user.role?.code})`} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={user.isActive ? 'success' : 'default'}
                        label={user.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isProtectedAdmin ? (
                        <>
                          <Tooltip title="Edit Protected Admin">
                            <IconButton size="small" color="primary" onClick={() => openEditDialog(user)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Protected Admin Password">
                            <IconButton size="small" color="warning" onClick={() => handleResetProtectedAdmin(user)}>
                              <LockResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No actions
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!listLoading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found for this school
                  </TableCell>
                </TableRow>
              )}
              {listLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading users...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editOpen} onClose={() => !editSaving && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Protected Admin</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData.firstName}
                onChange={(e) => setEditData((prev) => ({ ...prev, firstName: e.target.value }))}
                disabled={editSaving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData.lastName}
                onChange={(e) => setEditData((prev) => ({ ...prev, lastName: e.target.value }))}
                disabled={editSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={editData.email}
                onChange={(e) => setEditData((prev) => ({ ...prev, email: e.target.value }))}
                disabled={editSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username (School Login Username)"
                value={editData.username}
                onChange={(e) => setEditData((prev) => ({ ...prev, username: e.target.value }))}
                disabled={editSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editData.phone}
                onChange={(e) => setEditData((prev) => ({ ...prev, phone: e.target.value }))}
                disabled={editSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.isActive}
                    onChange={(e) => setEditData((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={editSaving}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateSchoolAdmin;

